import { useCallback, useEffect, useState } from "react";
import { BehaviorSubject } from "rxjs";
const observers = {};
/**
 * A wrapper for useState that will maintain the state for other components.
 * @param key A unique key to identify the state.
 * @returns The created state (same as useState) that will be maintained by key. The setter keeps its identity until the key changes.
 */
export const useMadState = (key, value) => {
    let subscription;
    let observer = observers[key];
    const _value = observer?.value || value;
    const [data, setDataState] = useState(typeof _value !== "undefined" ? _value : undefined);
    // `key` can change between renders
    const [resolvedKey, setResolvedKey] = useState(key);
    let currentData = data;
    if (resolvedKey !== key) {
        // Re-derive the value for the new key immediately, rather than returning `data` for the
        // previous key until the effect below re-subscribes on the next render.
        setResolvedKey(key);
        currentData = typeof _value !== "undefined" ? _value : undefined;
        setDataState(currentData);
    }
    if (!observer) {
        observers[key] = new BehaviorSubject(typeof currentData !== "undefined" ? currentData : undefined);
        observer = observers[key];
    }
    useEffect(() => {
        if (!observer) {
            return undefined;
        }
        subscription = observer.subscribe(setDataState);
        return () => {
            subscription.unsubscribe();
            // Becuase `useEffect` is deferred, this cleanup logic can happen while a subscription is imminent.
            // `setTimeout` defers the logic until after the `useEffect`
            setTimeout(() => {
                if (!observer.observed) {
                    delete observers[key];
                }
            });
        };
    }, [observer]);
    // Bound to the key's observer rather than the latest key, so a setter captured before a key change
    // (e.g., by a pending request) can't write into the new key's state.
    const setData = useCallback((d) => observer.next(typeof d === "function" ? d(observer.value) : d), [observer]);
    return [currentData, setData];
};
/**
 * Reads the current state for a key without subscribing to it, so the caller neither re-renders on changes nor keeps the state alive.
 * @param key The key used with {@link useMadState}.
 * @returns The current state, or undefined once no component is using the key.
 */
export const getMadState = (key) => observers[key]?.value;
export const getMadStateObserverCount = () => Object.keys(observers).length;
//# sourceMappingURL=madState.js.map